<script lang="ts">
export default {
  name: 'AnnouncementBanners',
};
</script>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import { AlertCircle } from '@lucide/vue';
import Alert from '@/components/v2/Alert.vue';
import { Announcement } from '@/types/Announcement';
import { useAnnouncements } from '@/composables/useAnnouncements';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import Store from '@/store';

const stackRef = ref<HTMLElement | null>(null);
const { activeAnnouncements, load, dismiss } = useAnnouncements();
const { updateBannerHeight } = useHeaderOffset();

let observer: ResizeObserver | null = null;

function measure(): void {
  updateBannerHeight(stackRef.value?.offsetHeight ?? 0);
}

async function handleClose(announcement: Announcement): Promise<void> {
  await dismiss(announcement);
}

onMounted(async () => {
  // App mounts this on public routes too (e.g. /signout), where the request can only 401.
  if (!Store.state.user) {
    return;
  }
  await load();

  await nextTick();
  if (stackRef.value) {
    observer = new ResizeObserver(measure);
    observer.observe(stackRef.value);
  }
  measure();
});

// The stack unmounts its own element once the last announcement is dismissed, so the
// observer stops firing and the reserved height has to be released explicitly.
watch(
  () => activeAnnouncements.value.length,
  async (length) => {
    await nextTick();
    if (length === 0) {
      updateBannerHeight(0);
    } else {
      measure();
    }
  },
);

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  updateBannerHeight(0);
});
</script>

<template>
  <div
    v-if="activeAnnouncements.length"
    ref="stackRef"
    class="fixed top-0 right-0 left-0 z-1050"
    data-testid="announcement-banners">
    <Alert
      v-for="announcement in activeAnnouncements"
      :key="announcement.id"
      :type="announcement.severity"
      :icon="AlertCircle"
      closable
      class="rounded-none border-x-0"
      :data-testid="`announcement-${announcement.type}`"
      @close="handleClose(announcement)">
      <span class="font-normal">{{ announcement.text }}</span>
    </Alert>
  </div>
</template>
